import { useCallback, useRef, useState } from 'react';
import { SoundManager } from '../lib/SoundManager';

const AUDIO_BASE = process.env.REACT_APP_AUDIO_BASE_URL;

// In-memory cache: avoid re-downloading the same audio
const audioCache = new Map();

/**
 * Hook for playing pre-generated S3 audio with browser TTS fallback.
 * 
 * Usage:
 *   const { play, stop, isPlaying } = useS3Audio();
 *   play('detective', item.item_id, item.audio_text_fr);
 * 
 * - game: 'detective' | 'horloge_folle' | 'robot_balayeur' | 'la_course'
 * - itemId: the item_id from content data
 * - fallbackText: French text for browser TTS fallback (optional)
 */
export function useS3Audio() {
  const [isPlaying, setIsPlaying] = useState(false);
  const currentAudioRef = useRef(null);
  const currentUrlRef = useRef(null);

  const stop = useCallback(() => {
    const audio = currentAudioRef.current;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      audio.currentTime = 0;
    }
    currentAudioRef.current = null;
    currentUrlRef.current = null;
    try { speechSynthesis.cancel(); } catch (_) {}
    setIsPlaying(false);
  }, []);

  const play = useCallback((game, itemId, fallbackText, playbackRate = 1.0) => {
    if (SoundManager.isMuted()) { stop(); return; }
    if (!AUDIO_BASE || !game || !itemId) {
      stop();
      if (fallbackText) playFallbackTTS(fallbackText, setIsPlaying);
      return;
    }

    const url = `${AUDIO_BASE}/${game}/${itemId}.mp3`;

    // If same audio is requested, just restart it (no stop/recreate race condition)
    const current = currentAudioRef.current;
    if (current && currentUrlRef.current === url) {
      current.currentTime = 0;
      current.playbackRate = playbackRate;
      setIsPlaying(true);
      current.play().catch(() => {
        setIsPlaying(false);
        if (fallbackText) playFallbackTTS(fallbackText, setIsPlaying);
      });
      return;
    }

    // Different audio — stop current first
    if (current) {
      current.onended = null;
      current.onerror = null;
      current.pause();
      current.currentTime = 0;
    }
    try { speechSynthesis.cancel(); } catch (_) {}

    // Get or create Audio element
    let audio = audioCache.get(url);
    if (!audio) {
      audio = new Audio(url);
      audio.preload = 'auto';
      audioCache.set(url, audio);
    }

    audio.playbackRate = playbackRate;
    audio.currentTime = 0;
    currentAudioRef.current = audio;
    currentUrlRef.current = url;

    audio.onended = () => {
      setIsPlaying(false);
      currentAudioRef.current = null;
      currentUrlRef.current = null;
    };
    audio.onerror = () => {
      setIsPlaying(false);
      currentAudioRef.current = null;
      currentUrlRef.current = null;
      audioCache.delete(url);
      if (fallbackText) playFallbackTTS(fallbackText, setIsPlaying);
    };

    setIsPlaying(true);
    audio.play().catch(() => {
      setIsPlaying(false);
      currentAudioRef.current = null;
      currentUrlRef.current = null;
      audioCache.delete(url);
      if (fallbackText) playFallbackTTS(fallbackText, setIsPlaying);
    });
  }, [stop]);

  return { play, stop, isPlaying };
}

/**
 * Browser SpeechSynthesis fallback for when S3 audio is unavailable.
 */
function playFallbackTTS(text, setIsPlaying) {
  if (!text) return;
  try {
    setIsPlaying(true);
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'fr-CA';
    u.rate = 0.9;
    u.onend = () => setIsPlaying(false);
    u.onerror = () => setIsPlaying(false);
    speechSynthesis.speak(u);
  } catch (_) {
    setIsPlaying(false);
  }
}
